import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase/config';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import { Bell, Check, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const NotificationsComponent = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const { currentUser, userRole } = useAuth();
  const navigate = useNavigate();
  
  const open = Boolean(anchorEl);
  
  useEffect(() => {
    if (!currentUser) return;
    
    let notificationsQuery;
    
    if (userRole === 'admin' || userRole === 'PDG') {
      // Requête pour les administrateurs
      notificationsQuery = query(
        collection(db, 'notifications'),
        where('type', '==', 'admin_notification'),
        orderBy('createdAt', 'desc')
      );
    } else {
      // Requête pour les agriculteurs
      notificationsQuery = query(
        collection(db, 'notifications'),
        where('type', '==', 'farmer_notification'),
        where('farmerId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
    }
    
    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      const notificationsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));
      
      setNotifications(notificationsList);
      setUnreadCount(notificationsList.filter(n => !n.read).length);
    });
    
    return () => unsubscribe();
  }, [currentUser, userRole]);
  
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const markAsRead = async (notificationId) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true
      });
    } catch (error) {
      console.error('Erreur lors du marquage de la notification comme lue:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    await markAsRead(notification.id);
    handleClose();

    const isAdminUser = userRole === 'admin' || userRole === 'PDG';
    if (!isAdminUser) return;

    const targetSection = notification.targetSection || (notification.category === 'diagnostic' ? 'diagnostics' : '');
    const targetId = notification.targetId || notification.diagnosticId || '';

    if (targetSection) {
      const url = targetId
        ? `/admin-dashboard?section=${encodeURIComponent(targetSection)}&targetId=${encodeURIComponent(targetId)}`
        : `/admin-dashboard?section=${encodeURIComponent(targetSection)}`;
      navigate(url);
    }
  };
  
  const markAllAsRead = async () => {
    try {
      const promises = notifications
        .filter(n => !n.read)
        .map(n => updateDoc(doc(db, 'notifications', n.id), { read: true }));
      
      await Promise.all(promises);
    } catch (error) {
      console.error('Erreur lors du marquage de toutes les notifications comme lues:', error);
    }
  };
  
  const getNotificationContent = (notification) => {
    if (notification.type === 'admin_notification') {
      if (notification.title || notification.body) {
        return {
          title: notification.title || 'Notification administrateur',
          message: notification.body || 'Nouvelle notification administrateur'
        };
      }

      if (notification.category === 'diagnostic') {
        return {
          title: 'Nouveau diagnostic AVG Consulting',
          message: notification.farmName
            ? `Un nouveau diagnostic a ete soumis pour "${notification.farmName}".`
            : 'Un nouveau diagnostic a ete soumis.'
        };
      }

      return {
        title: `Nouvelle annonce ${notification.action === 'created' ? 'créée' : 'modifiée'}`,
        message: `${notification.farmerName} a ${notification.action === 'created' ? 'créé' : 'modifié'} l'annonce "${notification.announcementTitle}"`
      };
    } else if (notification.type === 'farmer_notification') {
      return {
        title: `Annonce ${notification.status === 'approved' ? 'approuvée' : 'rejetée'}`,
        message: `Votre annonce "${notification.announcementTitle}" a été ${notification.status === 'approved' ? 'approuvée' : 'rejetée'}`
      };
    }
    
    return { title: 'Notification', message: 'Nouvelle notification' };
  };
  
  return (
    <>
      <IconButton
        onClick={handleClick}
        size="large"
        aria-label="notifications"
        color="inherit"
      >
        <Badge badgeContent={unreadCount} color="error">
          <Bell size={24} />
        </Badge>
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 400,
            overflow: 'auto'
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Notifications</Typography>
          {unreadCount > 0 && (
            <Typography 
              variant="body2" 
              color="primary" 
              sx={{ cursor: 'pointer' }}
              onClick={markAllAsRead}
            >
              Tout marquer comme lu
            </Typography>
          )}
        </Box>
        
        <Divider />
        
        {notifications.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2">Aucune notification</Typography>
          </MenuItem>
        ) : (
          notifications.map((notification) => {
            const { title, message } = getNotificationContent(notification);
            
            return (
              <MenuItem 
                key={notification.id} 
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  backgroundColor: notification.read ? 'inherit' : 'rgba(25, 118, 210, 0.08)',
                  '&:hover': {
                    backgroundColor: notification.read ? 'rgba(0, 0, 0, 0.04)' : 'rgba(25, 118, 210, 0.12)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: notification.read ? 'normal' : 'bold' }}>
                      {title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDistanceToNow(notification.createdAt, { addSuffix: true, locale: fr })}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {message}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    {notification.read ? (
                      <Check size={16} color="#4caf50" />
                    ) : (
                      <Clock size={16} color="#f57c00" />
                    )}
                    <Typography variant="caption" sx={{ ml: 0.5 }} color={notification.read ? 'success.main' : 'warning.main'}>
                      {notification.read ? 'Lu' : 'Non lu'}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            );
          })
        )}
      </Menu>
    </>
  );
};

export default NotificationsComponent;
